import { Component, Input, OnChanges } from "@angular/core";
import { CommonModule } from "@angular/common";
import { PolicyTrackerData, StatusChange } from "../policy-tracker.interfaces";
import { TrackerState } from "../policy-status.constants";

export type StepKind = "done" | "current" | "upcoming" | "activeEnd" | "rejected" | "expiredEnd";

export interface TrackerStep {
  label: string;
  date: string | null;
  prefix?: string;
  kind: StepKind;
  lineLeft: string;
  lineRight: string;
}

const STEP_COLORS: Record<StepKind, string> = {
  done: "#02c39a",
  activeEnd: "#02c39a",
  current: "#50327d",
  upcoming: "#e7e8ec",
  rejected: "#eb3055",
  expiredEnd: "#f9af39",
};

@Component({
  selector: "app-policy-tracker-timeline",
  imports: [CommonModule],
  templateUrl: "./policy-tracker-timeline.component.html",
  styleUrls: ["./policy-tracker-timeline.component.scss"],
})
export class PolicyTrackerTimelineComponent implements OnChanges {
  @Input() policy!: PolicyTrackerData;
  @Input() state: TrackerState = "in_review";

  steps: TrackerStep[] = [];

  ngOnChanges(): void {
    this.steps = this.buildSteps();
  }

  private buildSteps(): TrackerStep[] {
    const revision = this.policy?.latestSubmittedRevision?.revision;
    const submitted = this.policy?.createdAt || this.policy?.created_at || null;
    // The revision's approval timestamp wins over history — history can hold an
    // older VERIFIED entry from a previous submission.
    const approved = revision?.revision_status_changed_on || this.historyDate(["VERIFIED", "APPROVED"]) || this.policy?.revision_status_changed_on || null;
    const active = revision?.effective_date || this.policy?.effectiveDate || null;
    const expired = revision?.expiration_date || this.policy?.expirationDate || null;
    const ended = this.historyDate(["REJECT", "DECLIN", "CANCEL"]) || this.policy?.updatedAt || null;

    const done = (label: string, date: string | null): TrackerStep => ({ label, date, kind: "done", lineLeft: "", lineRight: "" });
    const step = (label: string, date: string | null, kind: StepKind, prefix?: string): TrackerStep => ({ label, date, kind, prefix, lineLeft: "", lineRight: "" });

    let steps: TrackerStep[];
    switch (this.state) {
      case "rejected":
      case "cancelled":
        steps = [
          done("Submitted", submitted),
          done("Under review", submitted),
          step(this.state === "rejected" ? "Rejected" : "Cancelled", ended, "rejected"),
        ];
        break;
      case "expired":
        steps = [
          done("Submitted", submitted),
          done("Under review", submitted),
          done("Approved", approved),
          done("Active", active),
          step("Expired", expired, "expiredEnd"),
        ];
        break;
      case "approved":
        steps = [
          done("Submitted", submitted),
          done("Under review", submitted),
          done("Approved", approved),
          step("Active", active, "upcoming", active ? "From" : undefined),
        ];
        break;
      case "active":
        steps = [
          done("Submitted", submitted),
          done("Under review", submitted),
          done("Approved", approved),
          step("Active", active, "activeEnd", "Since"),
        ];
        break;
      default: // in_review
        steps = [
          done("Submitted", submitted),
          step("Under review", submitted, "current", "Started"),
          step("Approved", null, "upcoming"),
          step("Active", null, "upcoming"),
        ];
    }

    const gray = STEP_COLORS.upcoming;
    steps.forEach((s, i) => {
      const color = STEP_COLORS[s.kind];
      s.lineLeft = i === 0 ? "transparent" : color === gray || STEP_COLORS[steps[i - 1].kind] === gray ? gray : color;
      s.lineRight = i === steps.length - 1 ? "transparent" : STEP_COLORS[steps[i + 1].kind];
    });
    return steps;
  }

  private historyDate(needles: string[]): string | null {
    const history = this.policy?.statusChangeHistory || [];
    const match = history.find((change: StatusChange) =>
      needles.some((needle) => (change.newStatus || "").toUpperCase().includes(needle))
    );
    return match?.date || null;
  }
}
